import React, { useEffect, useState } from "react";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import Card from "react-bootstrap/Card";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { deleteSource } from "helpers/leads/leadSources/deleteSources";

import EmptyState from "components/atoms/EmptyState";
import PageContainer from "components/atoms/PageContainer";
import AddEditSourceApi from "components/Leads/AddEditSourceApi";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import { ModalName } from "components/organisms/ModalRoot/ModalRoot.layout";
import SwalAlertComp from "components/SwalAlert";

import { openModal } from "../../../redux/slices/modal/modal.slice";
import { getSourceIcon } from "./LeadSources.helpers";

const LeadSources = ({
    language,
    getSourcesStatus,
    sources,
    dispatchGetLeadSources,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [editSourceModalData, setEditSourceModalData] = useState(null);
    const [deletingRow, setDeletingRow] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteRow, setPendingDeleteRow] = useState(null);

    const handleConfirmDelete = async () => {
        if (!pendingDeleteRow) return;
        setConfirmOpen(false);
        setDeletingRow(pendingDeleteRow);
        const response = await deleteSource({
            source_type_code: pendingDeleteRow.source_type,
            source_id: pendingDeleteRow.id,
        });
        setDeletingRow(null);
        setPendingDeleteRow(null);
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            dispatchGetLeadSources();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message,
                icon: "error",
            });
        }
    };

    useEffect(() => {
        dispatchGetLeadSources();
    }, []);

    const handleCreateButton = () => {
        navigate("/leads-wizard");
    };

    const handleEditSource = (row) => {
        if (row?.source_type === "sapi") {
            setEditSourceModalData(row);
        } else if (row?.source_type === "gglw") {
            dispatch(
                openModal({
                    modalName: ModalName.ADD_EDIT_LEAD_GOOGLE,
                    data: row,
                }),
            );
        }
    };

    const isLoading =
        getSourcesStatus === "idle" ||
        getSourcesStatus === "loading" ||
        getSourcesStatus === "failed";

    return (
        <PageContainer
            title={language?.sidebar?.sources ?? "Sources"}
            subtitle={
                language?.sources?.sources_desc ??
                "On this page, you can view sources, and create a new one by using the button."
            }
            additionalButtons={
                <Button
                    onClick={handleCreateButton}
                    color="primary"
                    variant="contained"
                >
                    {language?.sources?.add_source ?? "Add Source"}
                </Button>
            }
            loading={isLoading}
            variant="list"
            containerClassName="tracker-detail-area"
        >
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setPendingDeleteRow(null);
                }}
                onConfirm={handleConfirmDelete}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message="This action cannot be undone."
                confirmColor="error"
            />

            {!!editSourceModalData && (
                <AddEditSourceApi
                    data={editSourceModalData}
                    handleCloseSourceApiModal={() =>
                        setEditSourceModalData(null)
                    }
                    onSuccess={() => {
                        dispatchGetLeadSources();
                        setEditSourceModalData(null);
                    }}
                />
            )}

            {!sources?.length ? (
                <EmptyState
                    icon={<PublicOutlinedIcon />}
                    title={
                        language?.sources?.no_sources_yet ?? "No sources yet"
                    }
                    description={
                        language?.sources?.no_sources_yet_desc ??
                        "Add your first source to start collecting leads."
                    }
                />
            ) : (
            <Grid
                container
                rowSpacing={1}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            >
                {sources &&
                    Array.isArray(sources) &&
                    sources.map((item, index) => {
                        const isDeleting = deletingRow?.id === item.id;
                        const canEdit =
                            item?.source_type === "sapi" ||
                            item?.source_type === "gglw";
                        return (
                            <Grid
                                key={item?.id ?? index}
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 4,
                                }}
                            >
                                <Card
                                    style={{
                                        boxShadow: "none",
                                        border: "1px solid #E7E7E7",
                                        borderRadius: "10px",
                                        marginBottom: "20px",
                                        cursor: "default",
                                        position: "relative",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                    className={`dashboard-tree-source-item lead-item service-item no-effect dashboard-tree-source-item-${index}`}
                                >
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            display: "flex",
                                            gap: "5px",
                                            zIndex: 10,
                                        }}
                                    >
                                        {canEdit && (
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditSource(item);
                                                    }}
                                                    style={{
                                                        backgroundColor:
                                                            "#f5f5f5",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip
                                            title={
                                                item?.has_connections
                                                    ? "The source cannot be deleted due to an active connection."
                                                    : "Delete"
                                            }
                                        >
                                            <Box component="span">
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        item?.has_connections ||
                                                        isDeleting
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isDeleting) return;
                                                        setPendingDeleteRow(
                                                            item,
                                                        );
                                                        setConfirmOpen(true);
                                                    }}
                                                    style={{
                                                        backgroundColor:
                                                            "#f5f5f5",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    {isDeleting ? (
                                                        <CircularProgress
                                                            size={16}
                                                            color="error"
                                                        />
                                                    ) : (
                                                        <DeleteOutlineOutlinedIcon
                                                            fontSize="small"
                                                            color="error"
                                                        />
                                                    )}
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    </div>
                                    <CardContent
                                        style={{ padding: "20px" }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <div
                                            style={{
                                                width: "2rem",
                                                height: "2rem",
                                            }}
                                            className="mr-3"
                                        >
                                            <img
                                                src={getSourceIcon(
                                                    item?.source_type,
                                                )}
                                                alt={item?.source_type ?? ""}
                                            />
                                        </div>
                                        <Typography
                                            sx={{
                                                fontSize: 18,
                                                fontWeight: "700",
                                                fontFamily:
                                                    "Nunito, sans-serif",
                                                color: "black",
                                            }}
                                        >
                                            {item?.name}
                                        </Typography>
                                    </CardContent>

                                    <CardContent
                                        style={{
                                            padding: "20px",
                                            paddingTop: "0",
                                        }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <span
                                            style={{
                                                display: "block",
                                                fontSize: "14px",
                                                color: "black",
                                                fontWeight: "500",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "12px",
                                                    color: "#ABABAB",
                                                    fontWeight: "400",
                                                }}
                                            >
                                                {language?.source_modal
                                                    ?.source_type ??
                                                    "Source Type"}
                                            </span>
                                            {item?.source}
                                        </span>
                                    </CardContent>

                                    {item?.created_at && (
                                        <CardContent
                                            style={{
                                                padding: "20px",
                                                paddingTop: "0",
                                            }}
                                            className="d-flex items-center justify-start"
                                        >
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.lead
                                                        ?.create_on ??
                                                        "Create on"}
                                                </span>
                                                {item?.created_at}
                                            </span>
                                        </CardContent>
                                    )}

                                    <CardContent
                                        style={{
                                            padding: "20px",
                                            paddingTop: "0",
                                        }}
                                        className="d-flex items-center justify-start"
                                    >
                                        {item?.has_connections ? (
                                            <div className="in-use-area">
                                                {language?.destinations
                                                    ?.in_use ?? "In Use"}
                                            </div>
                                        ) : (
                                            <div className="not-in-use-area">
                                                {language?.destinations
                                                    ?.not_in_use ??
                                                    "Not In Use"}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
            </Grid>
            )}
        </PageContainer>
    );
};

export default LeadSources;
